import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faCircleXmark } from "@fortawesome/free-solid-svg-icons";
import Button from "../../../../components/Button";
import Modal from "../../../../components/Modal";
import SimpleTable from "../../../../components/SimpleTable";
import { useCompanyDateFormat, useCompanyTimeFormat } from "../../../../utils/hooks/company";

const actionLabels = {
    "open-shift": "move-to-open-shift",
    delete: "delete",
};

export const useShiftActionsResultModal = () => {
    const { t } = useTranslation();
    const dateFormat = useCompanyDateFormat();
    const timeFormat = useCompanyTimeFormat();
    const [state, setState] = useState();

    const open = useCallback((results) => results?.length
        ? new Promise((resolve) => setState({ results, resolve }))
        : Promise.resolve(), []);

    const close = useCallback(() => {
        state?.resolve();
        setState(undefined);
    }, [state]);

    const columns = useMemo(() => [
        {
            id: "date",
            header: t("date"),
            cell: ({ row: { original } }) => original.startDate
                ? moment.parseZone(original.startDate).format(dateFormat)
                : "-",
        },
        {
            id: "time",
            header: t("time"),
            cell: ({ row: { original } }) => original.startDate && original.endDate
                ? `${moment.parseZone(original.startDate).format(timeFormat)} - ${moment.parseZone(original.endDate).format(timeFormat)}`
                : "-",
        },
        {
            id: "job",
            header: t("job"),
            cell: ({ row: { original } }) => original.job?.displayName ?? "-",
        },
        {
            id: "location",
            header: t("location"),
            cell: ({ row: { original } }) => original.location?.displayName ?? "-",
        },
        {
            id: "action",
            header: t("actions"),
            cell: ({ row: { original } }) => t(actionLabels[original.action] ?? original.action),
        },
        {
            id: "status",
            header: t("status"),
            cell: ({ row: { original } }) => original.success ? (
                <span className="text-success font-weight-bold">
                    <FontAwesomeIcon icon={faCircleCheck} className="mr-1" />
                    {t("success")}
                </span>
            ) : (
                <span className="text-danger font-weight-bold">
                    <FontAwesomeIcon icon={faCircleXmark} className="mr-1" />
                    {t("failed")}
                </span>
            ),
        },
        {
            id: "message",
            header: t("message"),
            cell: ({ row: { original } }) => original.message || "-",
        },
    ], [t, dateFormat, timeFormat]);

    const data = useMemo(
        () => (state?.results ?? []).map((result, i) => ({ ...result, id: `${result.shiftId}-${i}` })),
        [state]
    );

    const modal = useMemo(() => (
        <Modal
            isOpen={!!state}
            toggle={close}
            title={t("shift-actions-results-title")}
            size="lg"
            centered
            footer={<Button color="primary" onClick={close}>{t("close")}</Button>}
        >
            <SimpleTable columns={columns} data={data} footer={false} />
        </Modal>
    ), [state, close, t, columns, data]);

    return { open, modal };
};
